import React, { useState } from 'react';
import { Card } from '@/lib/components/ui/card';
import { Button } from '@/lib/components/ui/button';
import { Plus } from 'lucide-react';

interface HoursContentProps {
  selectedHours: Set<string>;
  onHourRangeToggle: (hourId: string) => void;
  onHourRangeClick?: (hourId: string) => void;
  selectedAction: 'READ' | 'UPDATE' | 'DELETE' | undefined;
  onActionSelect: (action: 'READ' | 'UPDATE' | 'DELETE') => void;
  isPrivate: boolean;
  onPrivateToggle: (isPrivate: boolean) => void;
}

const hourRanges = [
  { id: 'business-hours', label: 'Business Hours', description: '9:00 AM - 5:00 PM', start: 9, end: 17 },
  { id: 'morning', label: 'Morning', description: '6:00 AM - 12:00 PM', start: 6, end: 12 },
  { id: 'afternoon', label: 'Afternoon', description: '12:00 PM - 6:00 PM', start: 12, end: 18 },
  { id: 'evening', label: 'Evening', description: '6:00 PM - 10:00 PM', start: 18, end: 22 },
  { id: 'night', label: 'Night', description: '10:00 PM - 6:00 AM', start: 22, end: 6 },
];

export const HoursContent: React.FC<HoursContentProps> = ({
  selectedHours,
  onHourRangeToggle,
  selectedAction,
  onActionSelect,
  isPrivate,
  onPrivateToggle,
}) => {
  const [customRanges, setCustomRanges] = useState<
    Array<{ id: string; label: string; description: string; start: number; end: number }>
  >([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [fromTime, setFromTime] = useState('09:00');
  const [toTime, setToTime] = useState('17:00');
  const [customLabel, setCustomLabel] = useState('');

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleAddCustomRange = () => {
    if (!customLabel.trim()) return;

    const fromHour = parseInt(fromTime.split(':')[0]);
    const toHour = parseInt(toTime.split(':')[0]);

    const newRange = {
      id: `custom-${Date.now()}`,
      label: customLabel,
      description: `${formatTime(fromTime)} - ${formatTime(toTime)}`,
      start: fromHour,
      end: toHour,
    };

    setCustomRanges((prev) => [...prev, newRange]);
    setCustomLabel('');
    setFromTime('09:00');
    setToTime('17:00');
    setShowCustomForm(false);
  };

  const allRanges = [...hourRanges, ...customRanges];

  return (
    <div className="bg-gradient-to-br from-gray-50/30 via-white to-blue-50/20 p-8">
      <div className="animate-fade-in mb-8">
        <h2 className="bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-3xl font-bold text-transparent">
          Time Restrictions
        </h2>
        <p className="animate-fade-in animation-delay-100 mt-2 text-gray-600">
          Set time-based access restrictions and permissions
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {allRanges.map((range, index) => {
          const isSelected = selectedHours.has(range.id);

          return (
            <Card
              key={range.id}
              className={`group animate-fade-in relative cursor-pointer overflow-hidden border-2 bg-white/60 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/5 ${
                isSelected
                  ? 'scale-[1.02] transform border-blue-500/50 bg-gradient-to-br from-blue-50 to-blue-100/50 shadow-lg shadow-blue-100'
                  : 'border-gray-200/50 hover:border-blue-300 hover:bg-blue-50/30'
              }`}
              style={{ animationDelay: `${index * 100 + 200}ms` }}
              onClick={() => onHourRangeToggle(range.id)}
            >
              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute top-3 right-3">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 shadow-sm">
                    <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                  </div>
                </div>
              )}

              <div className="flex flex-col">
                <h3
                  className={`text-lg font-semibold transition-colors ${
                    isSelected ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'
                  }`}
                >
                  {range.label}
                </h3>
                <p
                  className={`mt-2 text-sm leading-relaxed transition-colors ${
                    isSelected ? 'text-blue-700' : 'text-gray-500 group-hover:text-gray-600'
                  }`}
                >
                  {range.description}
                </p>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
